import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import {
    COLORS,
    cardSx,
    FinanceKpiTile,
    FinanceDetailsSection,
} from "./finance/shared/FinanceUiKit";
import {
    EdmOverview,
    EdmStatusPill,
    EdmStatusSelect,
    EdmProfileSelect,
    matchesEdmFilters,
    readEdmFilterFromUrl,
    getEdmState,
} from "./finance/shared/edmStatus";
import { getVatAmount, hasVatIncluded } from "./finance/shared/vatDisplay";
import { useSnackbar } from "context";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
    Avatar,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Skeleton,
    TextField,
    Tooltip,
} from "@mui/material";

import {
    ArrowUpward,
    Delete,
    DirectionsBoat,
    Download,
    Edit,
    FilterAlt,
    Flight,
    LocalShipping,
    PictureAsPdf,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    Send,
    CloudDone,
    Visibility,
    WarningAmber,
} from "@mui/icons-material";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appConfirm, appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CheckCircle } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const normalizeInvoiceList = (payload) => {
    const candidates = [
        payload,
        payload?.data,
        payload?.invoices,
        payload?.results,
        payload?.docs,
        payload?.items,
        payload?.records,
        payload?.data?.data,
        payload?.data?.invoices,
        payload?.data?.results,
        payload?.data?.docs,
        payload?.data?.items,
        payload?.data?.records,
    ];

    const list = candidates.find(Array.isArray) || [];

    return list
        .map((item) => {
            if (!item || typeof item !== "object") return null;

            if (item.attributes && typeof item.attributes === "object") {
                return {
                    ...item.attributes,
                    id: item.id || item.attributes.id || item.attributes._id,
                    _id: item.attributes._id || item.id,
                };
            }

            return item;
        })
        .filter(Boolean);
};

const normalizeFinanceInvoiceRecord = (record) => {
    const item = record?.attributes || record || {};

    const shipmentNo =
        item?.shipmentNo ||
        item?.shipment_no ||
        item?.jobNo ||
        item?.job_no ||
        item?.invoiceNo ||
        item?.invoice_no ||
        "";


    return {
        ...item,
        _id: item?._id || item?.id || item?.invoiceId || item?.invoice_id || item?.financeJobId || item?.finance_job_id,
        id: item?.id || item?._id || item?.invoiceId || item?.invoice_id || item?.financeJobId || item?.finance_job_id,
        shipmentId:
            item?.shipmentId ||
            item?.shipment_id ||
            item?.jobId ||
            item?.job_id ||
            item?._id ||
            item?.id,
        shipmentNo,
        invoiceNo: item?.invoiceNo || item?.invoice_no || shipmentNo,
        customerName:
            item?.customerName ||
            item?.customer_name ||
            item?.customer?.companyName ||
            item?.customer ||
            item?.globalAgentName ||
            item?.serviceProviderName ||
            "N/A",
        invoiceItems:
            item?.invoiceItems ||
            item?.invoice_items ||
            item?.cargoDetails ||
            item?.cargo_details ||
            [],
        invoiceOrigin: item?.invoiceOrigin || item?.invoice_origin || "tms",
    };
};


const normalizeText = (value) =>
    String(value || "")
        .trim()
        .toLowerCase();

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

// The stored invoiceSalesAmount is the item subtotal before any invoice-level
// discount (manual invoices); revenue, profit and margin use it after the discount.
const getInvoiceSalesAmount = (invoice) =>
    Math.max(
        firstDefinedNumber(
            invoice?.invoiceSalesAmount,
            invoice?.salesAmount,
            invoice?.customerInvoiceAmount,
            invoice?.totalAmount,
            invoice?.amount,
        ) - Math.max(toNumber(invoice?.discountAmount), 0),
        0,
    );

// What the customer is billed: VAT included when the invoice was cut with VAT.
// getInvoiceSalesAmount stays ex-VAT because cost/profit/margin use it.
const getBilledSalesAmount = (invoice) =>
    hasVatIncluded(invoice)
        ? toNumber(invoice?.sellingRateInclVat)
        : getInvoiceSalesAmount(invoice);

const getInvoiceCurrency = (invoice) =>
    invoice?.currency ||
    invoice?.sellingRateCurrency ||
    invoice?.invoiceCurrency ||
    invoice?.financialSummary?.totalCurrency ||
    "EUR";

const getInvoiceCostCurrency = (invoice) => {
    const rawItems = Array.isArray(invoice?.invoiceItems)
        ? invoice.invoiceItems
        : Array.isArray(invoice?.invoiceItems?.data)
            ? invoice.invoiceItems.data
            : [];
    const itemWithCurrency = rawItems
        .map((item) => item?.attributes || item)
        .find((item) => item?.costCurrency);

    return (
        invoice?.costCurrency ||
        invoice?.totalCostCurrency ||
        itemWithCurrency?.costCurrency ||
        invoice?.financialSummary?.costCurrency ||
        invoice?.financialSummary?.totalCurrency ||
        "EUR"
    );
};

const normalizeInvoiceItems = (invoice) => {
    const source =
        invoice?.invoiceItems ||
        invoice?.cargoDetails ||
        invoice?.attributes?.invoiceItems ||
        [];

    const rawItems = Array.isArray(source)
        ? source
        : Array.isArray(source?.data)
            ? source.data
            : Array.isArray(source?.items)
                ? source.items
                : [];

    const sellingCurrency = getInvoiceCurrency(invoice);
    const costCurrency = getInvoiceCostCurrency(invoice);

    const items = rawItems.map((rawItem) => {
        const item = rawItem?.attributes || rawItem || {};
        const quantity = firstDefinedNumber(
            item.unitQuantity,
            item.quantity,
            item.qty,
            1,
        ) || 1;
        const unitPrice = firstDefinedNumber(
            item.unitPrice,
            item.sellingRate,
            item.salesPrice,
            quantity > 0 ? toNumber(item.amount) / quantity : 0,
        );

        return {
            ...item,
            commodity:
                item?.commodity?.label ||
                item?.commodity ||
                item?.label ||
                "Freight Charge",
            description: item.description || "",
            totalCost: firstDefinedNumber(
                item.totalCost,
                item.cost,
                item.unitCost,
                item.buyingRate,
            ),
            costCurrency:
                item.costCurrency ||
                item.totalCostCurrency ||
                item.buyingRateCurrency ||
                costCurrency,
            unitPrice,
            sellingRateCurrency:
                item.sellingRateCurrency ||
                item.salesCurrency ||
                item.currency ||
                sellingCurrency,
            unitQuantity: quantity,
            amount: unitPrice * quantity,
        };
    });

    if (items.length > 0) return items;

    return [
        {
            commodity: "Freight Charge",
            description: "Shipment freight service",
            totalCost: getTotalCost(invoice),
            costCurrency,
            unitPrice: getInvoiceSalesAmount(invoice),
            sellingRateCurrency: sellingCurrency,
            unitQuantity: 1,
            amount: getInvoiceSalesAmount(invoice),
        },
    ];
};

const getBuyingRate = (invoice) =>
    firstDefinedNumber(
        invoice?.buyingRate,
        invoice?.financialSummary?.buyingRate,
        invoice?.freightCharges?.buyingRate,
    );

const getInvoiceItemsCost = (invoice) => {
    const items = Array.isArray(invoice?.invoiceItems)
        ? invoice.invoiceItems
        : Array.isArray(invoice?.cargoDetails)
            ? invoice.cargoDetails
            : [];

    return items.reduce(
        (sum, item) =>
            sum +
            firstDefinedNumber(
                item?.totalCost,
                item?.cost,
                item?.unitCost,
                item?.buyingRate,
            ),
        0,
    );
};

const getOperationalCost = (invoice) => {
    const summary = invoice?.financialSummary || {};
    const freightCharges = invoice?.freightCharges || {};

    const explicitCost = firstDefinedNumber(
        invoice?.operationalCost,
        invoice?.actualCostTotal,
        summary?.operationalCost,
        summary?.totalCost,
    );

    if (explicitCost > 0) return explicitCost;

    const invoiceItemsCost = getInvoiceItemsCost(invoice);

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (invoiceItemsCost > 0) return invoiceItemsCost;
    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    return freightChargeCost;
};

const getTotalCost = (invoice) => {
    const explicit = firstDefinedNumber(
        invoice?.totalCost,
        invoice?.actualCostTotal,
        invoice?.financialSummary?.totalCost,
    );

    const includesBuyingRate =
        invoice?.totalCostIncludesBuyingRate === true ||
        invoice?.financialSummary?.totalCostIncludesBuyingRate === true;

    if (explicit > 0) {
        return includesBuyingRate ? explicit : explicit + getBuyingRate(invoice);
    }

    const invoiceItemsCost = getInvoiceItemsCost(invoice);
    if (invoiceItemsCost > 0) return invoiceItemsCost;

    return getOperationalCost(invoice) + getBuyingRate(invoice);
};

// `convert(amount, fromCurrency, toCurrency)` is optional. When the invoice's
// selling and cost currencies differ, profit is meaningless without converting
// one into the other first — passing a converter (see InvoiceDetails' local
// `convertCurrency`) fixes that; without one this falls back to the old raw
// subtraction (only correct when both currencies already match).
const getProfit = (invoice, convert) => {
    const revenue = getInvoiceSalesAmount(invoice);
    const cost = getTotalCost(invoice);

    if (typeof convert !== "function") return revenue - cost;

    const sellingCurrency = getInvoiceCurrency(invoice);
    const costCurrency = getInvoiceCostCurrency(invoice);

    if (sellingCurrency === costCurrency) return revenue - cost;

    const convertedCost = convert(cost, costCurrency, sellingCurrency);
    if (convertedCost === null || convertedCost === undefined) return null;

    return revenue - convertedCost;
};

const getMargin = (invoice, convert) => {
    const revenue = getInvoiceSalesAmount(invoice);
    if (revenue <= 0) return 0;

    const profit = getProfit(invoice, convert);
    return profit === null ? null : (profit / revenue) * 100;
};

const getInvoicePartyName = (invoice) =>
    invoice?.customerName ||
    invoice?.customer_name ||
    invoice?.customer ||
    invoice?.globalAgentName ||
    invoice?.serviceProviderName ||
    invoice?.globalAgent?.companyName ||
    invoice?.serviceProvider?.companyName ||
    "N/A";

const getInvoiceOrigin = (invoice) =>
    normalizeText(invoice?.invoiceOrigin || invoice?.invoice_origin || "tms") ===
    "manual"
        ? "manual"
        : "tms";

const getTransportMode = (invoice) => {
    if (getInvoiceOrigin(invoice) === "manual") {
        return "Manual";
    }

    const shipmentNo = String(invoice?.shipmentNo || "")
        .trim()
        .toUpperCase();

    // En güvenilir sinyal: shipment numarasının öneki (RHFL/OHFL/AHFL).
    if (shipmentNo.startsWith("AHFL")) return "Air";
    if (shipmentNo.startsWith("OHFL")) return "Ocean";
    if (shipmentNo.startsWith("RHFL")) return "Road";

    // İkinci güvenilir sinyal: sourceType/transportMode alanlarının kendisi.
    // productType/serviceType gibi serbest metin alanlarıyla KARIŞTIRILMAZ --
    // önceden "Airport Pickup" gibi bir serviceType veya "season"/"seafood"
    // içeren bir productType, Road/Ocean shipment'ları yanlışlıkla Air/Ocean
    // olarak etiketliyordu.
    const authoritative = normalizeText(
        invoice?.sourceType || invoice?.transportMode || "",
    );

    if (authoritative === "air") return "Air";
    if (authoritative === "ocean") return "Ocean";
    if (authoritative === "road") return "Road";

    // Son çare: sadece taşıma moduna özgü, serbest metin OLMAYAN alanlar
    // (Create*Shipment formlarındaki sabit dropdown değerleri).
    const modeSpecific = normalizeText(
        [
            invoice?.airTransportType,
            invoice?.oceanTransportType,
            invoice?.roadTransportType,
        ].join(" "),
    );

    if (modeSpecific.includes("air") || modeSpecific.includes("flight")) return "Air";
    if (
        modeSpecific.includes("ocean") ||
        modeSpecific.includes("fcl") ||
        modeSpecific.includes("lcl")
    ) {
        return "Ocean";
    }
    if (modeSpecific.includes("ftl") || modeSpecific.includes("ltl")) return "Road";

    return "Road";
};

const getShipmentType = (invoice) => {
    const source = normalizeText(
        [
            invoice?.oceanTransportType,
            invoice?.roadTransportType,
            invoice?.airTransportType,
            invoice?.serviceType,
            invoice?.productType,
            invoice?.shipmentType,
        ].join(" "),
    );

    if (source.includes("fcl")) return "FCL";
    if (source.includes("lcl")) return "LCL";
    if (source.includes("ftl")) return "FTL";
    if (source.includes("ltl")) return "LTL";
    if (source.includes("intermodal")) return "Intermodal";
    if (getTransportMode(invoice) === "Air") return "Air Freight";
    return "General";
};

const getShipmentPrefix = (invoice) => {
    const shipmentNo = String(invoice?.shipmentNo || "")
        .trim()
        .toUpperCase();

    if (shipmentNo.startsWith("OHFL")) return "OHFL";
    if (shipmentNo.startsWith("RHFL")) return "RHFL";
    if (shipmentNo.startsWith("AHFL")) return "AHFL";

    const mode = getTransportMode(invoice);
    if (mode === "Ocean") return "OHFL";
    if (mode === "Air") return "AHFL";
    return "RHFL";
};

const normalizeShipmentNo = (invoice) => {
    const shipmentNo = String(invoice?.shipmentNo || "")
        .trim()
        .toUpperCase();
    return shipmentNo.replace(/^(OHFL|RHFL|AHFL)/, "");
};

const getFullShipmentNo = (invoice) => {
    if (getInvoiceOrigin(invoice) === "manual") {
        return invoice?.invoiceNo || invoice?.invoice_no || "N/A";
    }

    const numericPart = normalizeShipmentNo(invoice);
    return numericPart
        ? `${getShipmentPrefix(invoice)}${numericPart}`
        : invoice?.invoiceNo || invoice?.invoice_no || "N/A";
};

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(toNumber(value));

// Sums invoice amounts grouped by currency (never blends them into one raw
// number here -- a plain sum across e.g. EUR + USD + TRY invoices would be
// meaningless as a single "€" total without converting first).
const sumByCurrency = (invoices, amountGetter, currencyGetter) => {
    const byCurrency = {};

    invoices.forEach((invoice) => {
        const currency = currencyGetter(invoice) || "EUR";
        byCurrency[currency] = (byCurrency[currency] || 0) + toNumber(amountGetter(invoice));
    });

    return byCurrency;
};

// Converts EVERY currency bucket into `targetCurrency` (even a single-entry
// bucket) and sums them -- for numeric comparisons like profit/margin, where
// revenue and cost need to land in the SAME currency even if each is
// individually single-currency (e.g. an invoice billed in EUR against a cost
// quoted in TRY: neither bucket alone is "mixed", but subtracting them
// without converting one into the other is still wrong). Returns null if a
// rate is missing, so callers can fall back to a raw/unconverted figure.
const sumConvertedTo = (byCurrency, convert, targetCurrency = "EUR") => {
    const entries = Object.entries(byCurrency).filter(([, amount]) => amount !== 0);
    if (entries.length === 0) return 0;
    if (typeof convert !== "function") return null;

    let total = 0;

    for (const [currency, amount] of entries) {
        const converted = convert(amount, currency, targetCurrency);
        if (converted === null || converted === undefined) return null;
        total += converted;
    }

    return total;
};

// Converts every currency bucket into `displayCurrency` and sums them into a
// single number in `displayCurrency` (the user's chosen currency -- see
// InvoiceDetails' Currency selector), using `convert(amount, from, to)` (see
// InvoiceDetails' local `convertCurrency`). Already-matching amounts pass
// through untouched. When conversion isn't possible (no converter, or a rate
// is missing) it falls back to showing the lone currency as-is if there's
// only one, or signals "can't blend" (`total: null`) for a genuine multi-
// currency mix, so callers can fall back to a per-currency breakdown instead
// of a wrong blended figure.
const blendByCurrency = (byCurrency, convert, displayCurrency = "EUR") => {
    const entries = Object.entries(byCurrency).filter(([, amount]) => amount !== 0);

    if (entries.length === 0) return { total: 0, resultCurrency: displayCurrency };

    if (entries.length === 1 && entries[0][0] === displayCurrency) {
        return { total: entries[0][1], resultCurrency: displayCurrency };
    }

    const fallback =
        entries.length === 1
            ? { total: entries[0][1], resultCurrency: entries[0][0] }
            : { total: null, resultCurrency: null };

    if (typeof convert !== "function") return fallback;

    let total = 0;

    for (const [currency, amount] of entries) {
        const converted = convert(amount, currency, displayCurrency);
        if (converted === null || converted === undefined) return fallback;
        total += converted;
    }

    return { total, resultCurrency: displayCurrency };
};

// Renders a currency-grouped total: a single formatted amount when it can be
// blended (see blendByCurrency), or each currency's amount joined together
// when it can't, so the figure never silently mislabels e.g. a TRY total as
// EUR.
const formatByCurrency = (
    byCurrency,
    convert,
    displayCurrency = "EUR",
    formatter = formatCompact,
) => {
    const { total, resultCurrency } = blendByCurrency(byCurrency, convert, displayCurrency);
    if (total !== null) return formatter(total, resultCurrency);

    return Object.entries(byCurrency)
        .filter(([, amount]) => amount !== 0)
        .map(([currency, amount]) => formatter(amount, currency))
        .join(" + ");
};

const formatMoney = (value, currency = "EUR") =>
    new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(toNumber(value));

const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const MODE_CONFIG = {
    Ocean: {
        icon: DirectionsBoat,
        color: COLORS.blue,
        background: COLORS.blueLight,
        border: "#B2DDFF",
    },
    Road: {
        icon: LocalShipping,
        color: COLORS.amber,
        background: COLORS.amberLight,
        border: "#FEDF89",
    },
    Air: {
        icon: Flight,
        color: COLORS.purple,
        background: COLORS.purpleLight,
        border: "#D9D6FE",
    },
    Manual: {
        icon: ReceiptLong,
        color: COLORS.subtext,
        background: "#F2F4F7",
        border: COLORS.border,
    },
};

// Görünüm Finance kitindeki KPI kartıyla aynı (bkz. shared/FinanceUiKit.js)
const MetricCard = (props) => <FinanceKpiTile {...props} />;

const TransportInvoiceCard = ({ mode, summary, convertCurrency, displayCurrency }) => {
    const config = MODE_CONFIG[mode] || MODE_CONFIG.Manual;
    const Icon = config.icon;

    return (
        <Card sx={{ ...cardSx, height: "100%" }}>
            <MDBox p={2.35}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <MDBox
                            width={42}
                            height={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color: config.color,
                                background: config.background,
                                border: `1px solid ${config.border}`,
                            }}
                        >
                            <Icon />
                        </MDBox>
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {`${financeText(mode)} ${financeText("Invoices")}`}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {summary.count} {financeText("records")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <Chip
                        size="small"
                        label={`${summary.share.toFixed(1)}% ${financeText("share")}`}
                        sx={{
                            color: config.color,
                            background: config.background,
                            fontWeight: 700,
                        }}
                    />
                </MDBox>

                <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Revenue")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.text }}
                        >
                            {formatByCurrency(summary.revenueByCurrency, convertCurrency, displayCurrency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Profit")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: summary.profit >= 0 ? COLORS.green : COLORS.red }}
                        >
                            {formatByCurrency(summary.profitByCurrency, convertCurrency, displayCurrency)}
                        </MDTypography>
                    </Grid>
                </Grid>

                <LinearProgress
                    variant="determinate"
                    value={Math.min(Math.max(summary.margin, 0), 100)}
                    sx={{
                        mt: 2,
                        height: 7,
                        borderRadius: "999px",
                        backgroundColor: "#F2F4F7",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: "999px",
                            background: config.color,
                        },
                    }}
                />
                <MDBox display="flex" justifyContent="space-between" mt={0.75}>
                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                        {financeText("Margin")}
                    </MDTypography>
                    <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: config.color }}
                    >
                        {summary.margin.toFixed(1)}%
                    </MDTypography>
                </MDBox>
            </MDBox>
        </Card>
    );
};

const InvoiceDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [search, setSearch] = useState("");
    const [transportMode, setTransportMode] = useState("all");
    const [pdfStatus, setPdfStatus] = useState("all");
    // EDM (e-Fatura / e-Arşiv) gönderim filtresi; Finance Dashboard'dan ?edm=notSent ile gelinebilir
    const [edmFilter, setEdmFilter] = useState(() => readEdmFilterFromUrl(location.search));
    const [edmProfile, setEdmProfile] = useState("all");
    const [deletingId, setDeletingId] = useState(null);
    const [sendingEdmId, setSendingEdmId] = useState(null);
    const [pendingEdmSend, setPendingEdmSend] = useState(null);
    const [checkingGibStatusId, setCheckingGibStatusId] = useState(null);
    const [exchangeRates, setExchangeRates] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const token = localStorage.getItem("token");

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    // Per-invoice profit/margin need this whenever selling currency and cost
    // currency differ (common for ocean/air, where carrier cost is often USD
    // but the customer is billed EUR/TRY) -- see getProfit/getMargin above.
    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = Number(amount) || 0;
            const from = String(fromCurrency || "").toUpperCase();
            const to = String(toCurrency || "").toUpperCase();

            if (!from || !to || from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const fetchInvoices = useCallback(async () => {
        try {
            setLoading(true);
            const baseUrl = String(API_URL || "").replace(/\/+$/, "");

            const fetchList = async (path) => {
                const response = await fetch(`${baseUrl}${path}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(
                        `${path} fetch failed (${response.status}): ${errorText || response.statusText}`,
                    );
                }

                return normalizeInvoiceList(await response.json());
            };

            const sources = await Promise.allSettled([
                fetchList("/invoice"),
                fetchList("/finance/jobs"),
                fetchList("/finance/manual-invoices?invoiceType=sales&limit=1000"),
            ]);

            const [invoiceResult, financeJobsResult, manualResult] = sources;
            const tmsInvoices = invoiceResult.status === "fulfilled" ? invoiceResult.value : [];
            const financeJobs = financeJobsResult.status === "fulfilled" ? financeJobsResult.value : [];
            const manualInvoices = manualResult.status === "fulfilled" ? manualResult.value : [];

            sources.forEach((result, index) => {
                if (result.status === "rejected") {
                    const labels = ["/invoice", "/finance/jobs", "/finance/manual-invoices"];
                    console.warn(`${labels[index]} could not be loaded:`, result.reason);
                }
            });

            const uniqueInvoices = new Map();

            const getRecordKey = (invoice, index) => {
                // A shipment that has already been invoiced shows up in BOTH
                // /finance/jobs (its own job record, no real invoiceNo) and
                // /invoice (the actual invoice, with a distinct invoiceNo).
                // normalizeFinanceInvoiceRecord falls back to shipmentNo for
                // the job's "invoiceNo" when there isn't a real one, so
                // keying on invoiceNo first made those two records land
                // under different keys and both survived as separate rows —
                // double-counting that shipment's revenue/cost/profit. Key
                // on the shared shipment identity first so the job record
                // and its invoice merge into one row; only fall back to the
                // invoice number for records with no shipment link at all
                // (e.g. manual invoices).
                const shipmentKey =
                    invoice?.shipmentId ||
                    invoice?.shipment_id ||
                    invoice?.shipmentNo ||
                    invoice?.shipment_no;

                if (shipmentKey) {
                    return `SHIPMENT:${String(shipmentKey).trim().toUpperCase()}`;
                }

                const businessKey =
                    invoice?.invoiceNo ||
                    invoice?.invoice_no ||
                    invoice?.jobNo ||
                    invoice?.job_no;

                if (businessKey) {
                    return `NUMBER:${String(businessKey).trim().toUpperCase()}`;
                }

                const id =
                    invoice?._id ||
                    invoice?.id ||
                    invoice?.invoiceId ||
                    invoice?.invoice_id ||
                    invoice?.financeJobId ||
                    invoice?.finance_job_id;

                return id ? `ID:${String(id)}` : `INVOICE-${index}`;
            };

            const upsertInvoice = (rawInvoice, index, source) => {
                const invoice = normalizeFinanceInvoiceRecord(rawInvoice);
                const key = getRecordKey(invoice, index);
                const previous = uniqueInvoices.get(key) || {};

                uniqueInvoices.set(key, {
                    ...previous,
                    ...invoice,
                    invoiceItems:
                        invoice?.invoiceItems?.length
                            ? invoice.invoiceItems
                            : previous?.invoiceItems || [],
                    __sources: Array.from(
                        new Set([...(previous?.__sources || []), source]),
                    ),
                });
            };

            // Finance jobs first, then /invoice overwrites with richer invoice data.
            financeJobs.forEach((invoice, index) => upsertInvoice(invoice, index, "finance-jobs"));
            tmsInvoices.forEach((invoice, index) => upsertInvoice(invoice, index, "invoice"));
            manualInvoices.forEach((invoice, index) => upsertInvoice(invoice, index, "manual"));

            setInvoices(Array.from(uniqueInvoices.values()));
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Invoice fetch error:", error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    // EDM dışındaki filtreler uygulanmış liste: EDM özet kartları bunun dağılımını gösterir
    const baseFilteredInvoices = useMemo(() => {
        const query = normalizeText(search);

        return invoices.filter((invoice) => {
            const matchesSearch =
                !query ||
                normalizeText(getFullShipmentNo(invoice)).includes(query) ||
                normalizeText(getInvoicePartyName(invoice)).includes(query) ||
                normalizeText(invoice?.email).includes(query) ||
                normalizeText(invoice?.createdBy).includes(query) ||
                normalizeText(invoice?.invoiceNo).includes(query);

            const matchesMode =
                transportMode === "all" || getTransportMode(invoice) === transportMode;

            const hasPdf = Boolean(invoice?.pdfUrl || invoice?.pdf_url);
            const matchesPdf =
                pdfStatus === "all" ||
                (pdfStatus === "available" && hasPdf) ||
                (pdfStatus === "missing" && !hasPdf);

            return matchesSearch && matchesMode && matchesPdf;
        });
    }, [invoices, search, transportMode, pdfStatus]);

    const filteredInvoices = useMemo(
        () => baseFilteredInvoices.filter((invoice) => matchesEdmFilters(invoice, edmFilter, edmProfile)),
        [baseFilteredInvoices, edmFilter, edmProfile],
    );

    // EDM kartlarındaki tutar: seçili para biriminde faturalanan toplam
    const formatEdmAmount = (list) =>
        formatByCurrency(sumByCurrency(list, getBilledSalesAmount, getInvoiceCurrency), convertCurrency, selectedCurrency);

    const stats = useMemo(() => {
        // Revenue/cost can each be in a different currency per invoice
        // (freight forwarders routinely invoice EUR ocean, TRY road, etc.).
        // Group by currency so the totals below never sum unlike currencies
        // into one number labeled with whichever currency happens to be
        // default.
        const revenueByCurrency = sumByCurrency(
            filteredInvoices,
            getInvoiceSalesAmount,
            getInvoiceCurrency,
        );
        const billedByCurrency = sumByCurrency(
            filteredInvoices,
            getBilledSalesAmount,
            getInvoiceCurrency,
        );
        const costByCurrency = sumByCurrency(
            filteredInvoices,
            getTotalCost,
            getInvoiceCostCurrency,
        );

        // totalRevenue/totalCost/totalProfit are only used below for the
        // margin *ratio* (never rendered directly as a currency amount —
        // that's revenueByCurrency/costByCurrency via formatByCurrency).
        // Convert both into EUR via convertCurrency so they land in the same
        // currency even when each is individually single-currency (e.g. all
        // revenue in EUR vs. all cost in TRY), falling back to the raw sum
        // only when a rate isn't available yet (exchange rates still loading).
        const totalRevenue =
            sumConvertedTo(revenueByCurrency, convertCurrency) ??
            filteredInvoices.reduce((sum, invoice) => sum + getInvoiceSalesAmount(invoice), 0);
        const totalCost =
            sumConvertedTo(costByCurrency, convertCurrency) ??
            filteredInvoices.reduce((sum, invoice) => sum + getTotalCost(invoice), 0);
        const totalProfit = totalRevenue - totalCost;
        const margin = totalRevenue ? (totalProfit / totalRevenue) * 100 : 0;
        const pdfCount = filteredInvoices.filter(
            (invoice) => invoice?.pdfUrl || invoice?.pdf_url,
        ).length;

        const profitByCurrency = {};
        new Set([...Object.keys(revenueByCurrency), ...Object.keys(costByCurrency)]).forEach(
            (currency) => {
                profitByCurrency[currency] =
                    (revenueByCurrency[currency] || 0) - (costByCurrency[currency] || 0);
            },
        );

        return {
            count: filteredInvoices.length,
            totalRevenue,
            totalCost,
            totalProfit,
            revenueByCurrency,
            billedByCurrency,
            costByCurrency,
            profitByCurrency,
            margin,
            pdfCount,
            missingPdf: filteredInvoices.length - pdfCount,
        };
    }, [filteredInvoices, convertCurrency]);

    const transportSummary = useMemo(() => {
        const base = {
            Ocean: { count: 0, revenue: 0, cost: 0, profit: 0, margin: 0, share: 0, revenueByCurrency: {}, profitByCurrency: {} },
            Road: { count: 0, revenue: 0, cost: 0, profit: 0, margin: 0, share: 0, revenueByCurrency: {}, profitByCurrency: {} },
            Air: { count: 0, revenue: 0, cost: 0, profit: 0, margin: 0, share: 0, revenueByCurrency: {}, profitByCurrency: {} },
            Manual: { count: 0, revenue: 0, cost: 0, profit: 0, margin: 0, share: 0, revenueByCurrency: {}, profitByCurrency: {} },
        };
        const costByCurrencyByMode = { Ocean: {}, Road: {}, Air: {}, Manual: {} };

        filteredInvoices.forEach((invoice) => {
            const mode = getTransportMode(invoice);
            const target = base[mode] || base.Manual;
            const costByCurrency = costByCurrencyByMode[mode] || costByCurrencyByMode.Manual;
            const revenue = getInvoiceSalesAmount(invoice);
            const cost = getTotalCost(invoice);
            const revenueCurrency = getInvoiceCurrency(invoice);
            const costCurrency = getInvoiceCostCurrency(invoice);

            target.count += 1;
            target.revenue += revenue;
            target.cost += cost;
            target.revenueByCurrency[revenueCurrency] =
                (target.revenueByCurrency[revenueCurrency] || 0) + revenue;
            costByCurrency[costCurrency] = (costByCurrency[costCurrency] || 0) + cost;
        });

        Object.keys(base).forEach((mode) => {
            const costByCurrency = costByCurrencyByMode[mode];

            // Convert each mode's revenue/cost into EUR for the margin
            // *ratio* (raw sum only as a fallback while exchange rates are
            // loading) -- same reasoning as `stats` above.
            const revenue =
                sumConvertedTo(base[mode].revenueByCurrency, convertCurrency) ?? base[mode].revenue;
            const cost = sumConvertedTo(costByCurrency, convertCurrency) ?? base[mode].cost;

            base[mode].profit = revenue - cost;
            base[mode].margin = revenue ? (base[mode].profit / revenue) * 100 : 0;
            base[mode].share = filteredInvoices.length
                ? (base[mode].count / filteredInvoices.length) * 100
                : 0;

            const profitByCurrency = {};
            new Set([
                ...Object.keys(base[mode].revenueByCurrency),
                ...Object.keys(costByCurrency),
            ]).forEach((currency) => {
                profitByCurrency[currency] =
                    (base[mode].revenueByCurrency[currency] || 0) - (costByCurrency[currency] || 0);
            });
            base[mode].profitByCurrency = profitByCurrency;
        });

        return base;
    }, [filteredInvoices, convertCurrency]);

    const shipmentTypeSummary = useMemo(() => {
        const result = filteredInvoices.reduce((accumulator, invoice) => {
            const type = getShipmentType(invoice);
            accumulator[type] = (accumulator[type] || 0) + 1;
            return accumulator;
        }, {});

        return Object.entries(result)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8);
    }, [filteredInvoices]);

    const handleNavigate = (shipmentId, invoice, mode = "edit") => {
        const invoiceId =
            invoice?._id ||
            invoice?.id ||
            invoice?.invoiceId ||
            invoice?.invoice_id;

        if (getInvoiceOrigin(invoice) === "manual") {
            if (!invoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            if (mode === "view") {
                navigate(`/invoice/finance/jobs/${invoiceId}`, {
                    state: {
                        job: invoice,
                        financeJobId: invoiceId,
                        invoiceOrigin: "manual",
                    },
                });
            } else {
                navigate(
                    `/invoice/finance/manual-invoices/${invoiceId}/edit`,
                    {
                        state: {
                            invoice,
                            invoiceId,
                            editMode: true,
                        },
                    }
                );
            }

            return;
        }

        if (!shipmentId) {
            appAlert(financeText("Shipment ID not found!"));
            return;
        }

        const invoiceCost = getOperationalCost(invoice);
        const invoiceBuyingRate = getBuyingRate(invoice);
        const totalCost = getTotalCost(invoice);
        const invoiceItems = normalizeInvoiceItems(invoice);
        const sellingAmount = getInvoiceSalesAmount(invoice);

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                viewMode: mode === "view",
                editMode: mode === "edit",
                // ViewShipments derives its own isEditMode (PUT vs POST) from
                // this field — without it, every "edit" here silently
                // created a brand-new duplicate invoice under the same
                // financeJobId instead of updating this one, and the
                // original (still stale) invoice kept showing everywhere.
                invoiceId: mode === "edit" ? invoiceId : undefined,
                shipments: [
                    {
                        ...invoice,
                        _id: shipmentId,
                        sourceType: getTransportMode(invoice).toLowerCase(),
                        shipmentNo: normalizeShipmentNo(invoice),
                        customerName: getInvoicePartyName(invoice),
                        customer: getInvoicePartyName(invoice),
                        globalAgentName:
                            invoice.globalAgentName || invoice.globalAgent?.companyName || "",
                        serviceProviderName:
                            invoice.serviceProviderName ||
                            invoice.serviceProvider?.companyName ||
                            "",
                        invoiceItems,
                        cargoDetails: invoiceItems,
                        sellingRateCurrency: getInvoiceCurrency(invoice),
                        costCurrency: getInvoiceCostCurrency(invoice),
                        totalCostCurrency: getInvoiceCostCurrency(invoice),
                        sellingRate: sellingAmount,
                        totalSellingRate: sellingAmount,
                        invoiceSalesAmount: sellingAmount,
                        salesAmount: sellingAmount,
                        totalAmount: sellingAmount,
                        actualRevenueTotal: sellingAmount,
                        expectedRevenueTotal: sellingAmount,
                        customerInvoiceAmount: sellingAmount,
                        operationalCost: invoiceCost,
                        buyingRate: invoiceBuyingRate,
                        actualCostTotal: totalCost,
                        expectedCostTotal: totalCost,
                        totalCost,
                        totalCostIncludesBuyingRate: true,
                        actualProfit: getProfit(invoice, convertCurrency),
                        expectedProfit: getProfit(invoice, convertCurrency),
                        actualMargin: getMargin(invoice, convertCurrency),
                        currency: getInvoiceCurrency(invoice),
                    },
                ],
            },
        });
    };

    const handleDelete = async (invoice) => {
        const invoiceId = invoice?._id || invoice?.id;
        if (!invoiceId) return;
        const isManual = getInvoiceOrigin(invoice) === "manual";
        const confirmationMessage = isManual
            ? financeText("Are you sure you want to cancel this manual invoice?")
            : financeText("Are you sure you want to delete this invoice?");

        if (!await appConfirm(confirmationMessage))
            return;

        try {
            setDeletingId(invoiceId);
            const path = isManual
                ? `/finance/manual-invoices/${invoiceId}/cancel`
                : `/invoice/${invoiceId}`;
            const response = await fetch(`${API_URL}${path}`, {
                method: isManual ? "PATCH" : "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                // The backend refuses to delete an Issued/paid/returned-
                // against invoice on purpose (see routes/invoice/index.js)
                // and explains exactly why — surface that instead of a
                // generic "could not be deleted" that hides the real reason.
                throw new Error(
                    financeText(payload?.error || payload?.message)
                        || financeText("Invoice could not be deleted."),
                );
            }

            setInvoices((previous) =>
                isManual
                    ? previous.map((current) =>
                        (current?._id || current?.id) === invoiceId
                            ? { ...current, invoiceStatus: "Cancelled" }
                            : current
                    )
                    : previous.filter(
                        (current) => (current?._id || current?.id) !== invoiceId,
                    ),
            );

            showSnackbar?.(
                payload?.message || financeText("Invoice deleted successfully."),
                "success",
            );
        } catch (error) {
            console.error("Delete error:", error);
            showSnackbar?.(error.message || financeText("Invoice could not be deleted."), "error");
        } finally {
            setDeletingId(null);
        }
    };

    const requestSendToEdm = (invoice) => {
        const invoiceId = invoice?._id || invoice?.id;
        if (!invoiceId) return;

        if (invoice?.edm?.status === "Sent") {
            showSnackbar?.(financeText("This invoice was already sent to EDM."), "warning");
            return;
        }

        setPendingEdmSend(invoice);
    };

    const handleSendToEdm = async () => {
        const invoice = pendingEdmSend;
        const invoiceId = invoice?._id || invoice?.id;
        if (!invoiceId) return;

        try {
            setSendingEdmId(invoiceId);
            setPendingEdmSend(null);
            const response = await fetch(
                `${API_URL}/edm/invoices/${invoiceId}/send`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result?.success) {
                throw new Error(result?.error || "EDM send failed");
            }

            setInvoices((previous) =>
                previous.map((current) =>
                    (current?._id || current?.id) === invoiceId
                        ? {
                            ...current,
                            edm: {
                                ...(current.edm || {}),
                                status: "Sent",
                                profile: result.profile,
                                invoiceId: result.invoiceId,
                                uuid: result.uuid,
                                sentAt: result.sentAt,
                                error: "",
                            },
                        }
                        : current,
                ),
            );

            showSnackbar?.(
                result.reconciled
                    ? financeText(
                        "This invoice had already reached EDM from an earlier attempt; its status was reconciled instead of resending.",
                    )
                    : `${financeText("Invoice sent to EDM successfully.")} (${result.profile})`,
                "success",
            );
        } catch (error) {
            console.error("EDM send error:", error);

            const errorMessage =
                error.message || financeText("Invoice could not be sent to EDM.");

            // The backend already persists edm.status = "Failed" on this
            // path (see routes/edm/index.js); reflect it locally too so the
            // row's send icon/tooltip show the failure immediately instead
            // of quietly staying "Not Sent" until the next full refresh.
            setInvoices((previous) =>
                previous.map((current) =>
                    (current?._id || current?.id) === invoiceId
                        ? {
                            ...current,
                            edm: {
                                ...(current.edm || {}),
                                status: "Failed",
                                error: errorMessage,
                            },
                        }
                        : current,
                ),
            );

            showSnackbar?.(errorMessage, "error");
        } finally {
            setSendingEdmId(null);
        }
    };

    // A successful send only confirms EDM's gateway accepted the message —
    // GİB can still reject it afterward. This re-queries EDM for that
    // actual downstream status instead of leaving "Sent" looking final.
    const handleCheckGibStatus = async (invoice) => {
        const invoiceId = invoice?._id || invoice?.id;
        if (!invoiceId) return;

        try {
            setCheckingGibStatusId(invoiceId);
            const response = await fetch(
                `${API_URL}/edm/invoices/${invoiceId}/status`,
                { headers: { Authorization: `Bearer ${token}` } },
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result?.success) {
                throw new Error(result?.error || financeText("GİB status could not be checked."));
            }

            setInvoices((previous) =>
                previous.map((current) =>
                    (current?._id || current?.id) === invoiceId
                        ? {
                            ...current,
                            edm: {
                                ...(current.edm || {}),
                                gibStatus: result.status || "",
                                gibStatusDescription: result.statusDescription || "",
                            },
                        }
                        : current,
                ),
            );

            showSnackbar?.(
                `${financeText("GİB status")}: ${result.status || financeText("Unknown")}${
                    result.statusDescription ? ` — ${result.statusDescription}` : ""
                }`,
                "warning",
            );
        } catch (error) {
            console.error("EDM get-status error:", error);
            showSnackbar?.(error.message || financeText("GİB status could not be checked."), "error");
        } finally {
            setCheckingGibStatusId(null);
        }
    };

    const exportCsv = () => {
        const headers = [
            "Shipment No",
            "Transport Mode",
            "Shipment Type",
            "Customer",
            "Email",
            "Invoice Items",
            "Created By",
            "Revenue",
            "Revenue (incl. VAT)",
            "Selling Currency",
            "Cost",
            "Cost Currency",
            "Profit",
            "Margin",
            "Date Sent",
            "PDF",
        ];

        const rows = filteredInvoices.map((invoice) => [
            getFullShipmentNo(invoice),
            getTransportMode(invoice),
            getShipmentType(invoice),
            getInvoicePartyName(invoice),
            invoice?.email || "",
            (invoice?.invoiceItems || [])
                .map((item) => item?.commodity)
                .filter(Boolean)
                .join(" | "),
            invoice?.createdBy || "N/A",
            getInvoiceSalesAmount(invoice),
            getBilledSalesAmount(invoice),
            getInvoiceCurrency(invoice),
            getTotalCost(invoice),
            getInvoiceCostCurrency(invoice),
            getProfit(invoice, convertCurrency) ?? "N/A",
            (() => {
                const margin = getMargin(invoice, convertCurrency);
                return margin === null ? "N/A" : `${margin.toFixed(2)}%`;
            })(),
            formatDateTime(invoice?.sentAt || invoice?.edm?.sentAt || invoice?.createdAt),
            invoice?.pdfUrl || invoice?.pdf_url || "",
        ]);

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "invoice-details.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const resetFilters = () => {
        setSearch("");
        setTransportMode("all");
        setPdfStatus("all");
        setEdmFilter("all");
        setEdmProfile("all");
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("SHIPMENT"),
                    accessor: "shipmentNo",
                    width: "15%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const mode = getTransportMode(invoice);
                        const config = MODE_CONFIG[mode] || MODE_CONFIG.Manual;
                        const Icon = config.icon;

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.1} py={0.35}>
                                <MDBox
                                    width={40}
                                    height={40}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        color: config.color,
                                        background: config.background,
                                        border: `1px solid ${config.border}`,
                                    }}
                                >
                                    <Icon sx={{ fontSize: "19px !important" }} />
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.navy,
                                            display: "block",
                                            maxWidth: 145,
                                            "&:hover": { color: COLORS.blue },
                                        }}
                                        onClick={() =>
                                            handleNavigate(
                                                invoice?.shipmentId || invoice?.shipment_id,
                                                invoice,
                                                "view",
                                            )
                                        }
                                    >
                                        {getFullShipmentNo(invoice)}
                                    </MDTypography>

                                    <MDBox display="flex" gap={0.6} mt={0.45}>
                                        <Chip
                                            size="small"
                                            label={financeText(mode)}
                                            sx={{
                                                height: 21,
                                                color: config.color,
                                                background: config.background,
                                                fontSize: "10px",
                                                fontWeight: 700,
                                            }}
                                        />
                                        <Chip
                                            size="small"
                                            label={getShipmentType(invoice)}
                                            sx={{
                                                height: 21,
                                                color: COLORS.navy,
                                                background: "#EEF2F6",
                                                fontSize: "10px",
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CUSTOMER"),
                    accessor: "customerName",
                    width: "16%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const name = getInvoicePartyName(invoice);

                        return (
                            <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>
                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.text, maxWidth: 155 }}
                                    >
                                        {name}
                                    </MDTypography>
                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.subtext, maxWidth: 155 }}
                                    >
                                        {invoice?.email || financeText("No email")}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("INVOICE ITEMS"),
                    accessor: "invoiceItems",
                    width: "16%",
                    Cell: ({ row }) => {
                        const items = row.original?.invoiceItems || [];
                        const visible = items.slice(0, 2);

                        return (
                            <MDBox>
                                {visible.length ? (
                                    visible.map((item, index) => (
                                        <MDTypography
                                            key={`${item?.commodity}-${index}`}
                                            variant="caption"
                                            display="block"
                                            sx={{ color: COLORS.text }}
                                        >
                                            • {item?.commodity || financeText("Unnamed item")}
                                        </MDTypography>
                                    ))
                                ) : (
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("No invoice items")}
                                    </MDTypography>
                                )}
                                {items.length > 2 && (
                                    <Chip
                                        size="small"
                                        label={`+${items.length - 2} ${financeText("more")}`}
                                        sx={{
                                            mt: 0.5,
                                            height: 21,
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            fontWeight: 700,
                                        }}
                                    />
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("FINANCIALS"),
                    accessor: "invoiceSalesAmount",
                    align: "right",
                    width: "15%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const currency = getInvoiceCurrency(invoice);
                        const costCurrency = getInvoiceCostCurrency(invoice);
                        const profit = getProfit(invoice, convertCurrency);
                        const margin = getMargin(invoice, convertCurrency);

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.blue }}
                                >
                                    {formatMoney(getBilledSalesAmount(invoice), currency)}
                                </MDTypography>
                                {hasVatIncluded(invoice) && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Incl. VAT")} {formatCompact(getVatAmount(invoice), currency)}
                                    </MDTypography>
                                )}
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Cost")} {formatCompact(getTotalCost(invoice), costCurrency)}
                                </MDTypography>
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    fontWeight="bold"
                                    sx={{ color: profit === null ? COLORS.subtext : profit >= 0 ? COLORS.green : COLORS.red }}
                                >
                                    {profit === null
                                        ? `${financeText("Profit")} — (${costCurrency}/${currency} rate unavailable)`
                                        : `${financeText("Profit")} ${formatCompact(profit, currency)} · ${margin.toFixed(1)}%`}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CREATED BY"),
                    accessor: "createdBy",
                    width: "10%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {row.original?.createdBy || "N/A"}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Invoice owner")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("DATE SENT"),
                    accessor: "sentAt",
                    width: "11%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {formatDateTime(
                                    row.original?.sentAt ||
                                    row.original?.edm?.sentAt ||
                                    row.original?.createdAt,
                                )}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {row.original?.sentAt || row.original?.edm?.sentAt
                                    ? financeText("Delivery timestamp")
                                    : financeText("Created (not sent yet)")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    // EDM gönderim durumu: etiket + gönderim zamanı / profil, hata ve GİB detayı ipucunda
                    Header: financeText("EDM STATUS"),
                    id: "edmStatus",
                    accessor: (row) => getEdmState(row),
                    width: "11%",
                    Cell: ({ row }) => <EdmStatusPill invoice={row.original} />,
                },
                {
                    Header: financeText("PDF"),
                    accessor: "pdfUrl",
                    align: "center",
                    width: "8%",
                    Cell: ({ row }) => {
                        const pdfUrl = row.original?.pdfUrl || row.original?.pdf_url;
                        return pdfUrl ? (
                            <Tooltip title={financeText("Open invoice PDF")} arrow>
                                <IconButton
                                    size="small"
                                    component="a"
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: COLORS.red,
                                        background: COLORS.redLight,
                                        border: "1px solid #FECDCA",
                                        "&:hover": { background: "#FEE4E2" },
                                    }}
                                >
                                    <PictureAsPdf sx={{ fontSize: "18px !important" }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Chip
                                size="small"
                                label={financeText("Missing")}
                                sx={{
                                    color: COLORS.amber,
                                    background: COLORS.amberLight,
                                    fontWeight: 700,
                                }}
                            />
                        );
                    },
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "10%",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const id = invoice?._id || invoice?.id;
                        const shipmentId = invoice?.shipmentId || invoice?.shipment_id;

                        return (
                            <MDBox display="flex" justifyContent="flex-end" gap={0.55}>
                                <Tooltip title={financeText("View invoice")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleNavigate(shipmentId, invoice, "view")}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            border: "1px solid #B2DDFF",
                                            "&:hover": { background: "#D1E9FF" },
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={financeText("Edit invoice")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleNavigate(shipmentId, invoice, "edit")}
                                        sx={{
                                            color: COLORS.purple,
                                            background: COLORS.purpleLight,
                                            border: "1px solid #D9D6FE",
                                            "&:hover": { background: "#E9E7FD" },
                                        }}
                                    >
                                        <Edit sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>
                                {invoice?.invoiceType !== "purchase" &&
                                    (!invoice?.documentType ||
                                        invoice.documentType === "invoice") &&
                                    invoice?.invoiceStatus === "Issued" && (() => {
                                        const edmSent = invoice?.edm?.status === "Sent";
                                        const edmFailed = invoice?.edm?.status === "Failed";
                                        const tooltipTitle = edmSent
                                            ? `${financeText("Sent to EDM")} (${invoice.edm.profile || ""})`
                                            : edmFailed
                                                ? `${financeText("EDM send failed")}: ${invoice.edm.error || ""}`
                                                : financeText("Send to EDM (e-Fatura/e-Arşiv)");

                                        return (
                                        <Tooltip title={tooltipTitle} arrow>
                                            <span>
                                                <IconButton
                                                    disabled={sendingEdmId === id || edmSent}
                                                    size="small"
                                                    onClick={() => requestSendToEdm(invoice)}
                                                    sx={{
                                                        color: edmSent
                                                            ? COLORS.green
                                                            : edmFailed
                                                                ? COLORS.red
                                                                : COLORS.blue,
                                                        background: edmSent
                                                            ? COLORS.greenLight
                                                            : edmFailed
                                                                ? COLORS.redLight
                                                                : COLORS.blueLight,
                                                        border: `1px solid ${
                                                            edmSent
                                                                ? "#ABEFC6"
                                                                : edmFailed
                                                                    ? "#FECDCA"
                                                                    : "#B2DDFF"
                                                        }`,
                                                        "&:hover": {
                                                            background: edmSent
                                                                ? "#DCFAE6"
                                                                : edmFailed
                                                                    ? "#FEE4E2"
                                                                    : "#D1E9FF",
                                                        },
                                                        "&.Mui-disabled": {
                                                            background: "#F2F4F7",
                                                            borderColor: COLORS.border,
                                                        },
                                                    }}
                                                >
                                                    {edmSent ? (
                                                        <CloudDone sx={{ fontSize: "17px !important" }} />
                                                    ) : edmFailed ? (
                                                        <WarningAmber sx={{ fontSize: "17px !important" }} />
                                                    ) : (
                                                        <Send sx={{ fontSize: "17px !important" }} />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        );
                                    })()}
                                {invoice?.edm?.status === "Sent" && (
                                    <Tooltip
                                        title={
                                            invoice?.edm?.gibStatus
                                                ? `${financeText("Last checked GİB status")}: ${invoice.edm.gibStatus}${
                                                    invoice.edm.gibStatusDescription ? ` — ${invoice.edm.gibStatusDescription}` : ""
                                                }`
                                                : financeText("Check GİB Status")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                disabled={checkingGibStatusId === id}
                                                size="small"
                                                onClick={() => handleCheckGibStatus(invoice)}
                                                sx={{
                                                    color: COLORS.navy,
                                                    background: "#EEF2F6",
                                                    border: "1px solid #CDD5DF",
                                                    "&:hover": { background: "#E3E8EF" },
                                                }}
                                            >
                                                <Refresh sx={{ fontSize: "17px !important" }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                <Tooltip title={financeText("Delete invoice")} arrow>
                  <span>
                    <IconButton
                        disabled={deletingId === id}
                        size="small"
                        onClick={() => handleDelete(invoice)}
                        sx={{
                            color: COLORS.red,
                            background: COLORS.redLight,
                            border: "1px solid #FECDCA",
                            "&:hover": { background: "#FEE4E2" },
                            "&.Mui-disabled": {
                                background: "#F2F4F7",
                                borderColor: COLORS.border,
                            },
                        }}
                    >
                      <Delete sx={{ fontSize: "17px !important" }} />
                    </IconButton>
                  </span>
                                </Tooltip>
                            </MDBox>
                        );
                    },
                },
            ],
            rows: filteredInvoices,
        }),
        [filteredInvoices, deletingId, sendingEdmId, checkingGibStatusId, financeLanguage],
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{ background: "rgba(255,255,255,.12)" }}
                                    >
                                        <WorkspacePremium sx={{ color: "#FFFFFF", fontSize: 20 }} />
                                    </MDBox>
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE INVOICE OPERATIONS")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{ color: "#FFFFFF", lineHeight: 1.12 }}
                                >
                                    {financeText("Invoice Details")}
                                </MDTypography>
                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{ color: "rgba(255,255,255,.72)", maxWidth: 720 }}
                                >
                                    {financeText(
                                        "Review customer invoices, transport modes, shipment items, profitability, document status and operational ownership from one professional workspace."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={<CheckCircle sx={{ color: "#75E0A7 !important" }} />}
                                        label={`${filteredInvoices.length} ${financeText("invoice records")}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                    <Chip
                                        icon={
                                            <CalendarMonth sx={{ color: "#B2DDFF !important" }} />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: "rgba(255,255,255,.72)" }}
                                        >
                                            {financeText("TOTAL INVOICE REVENUE")}
                                        </MDTypography>
                                        <ReceiptLong sx={{ color: "#B2DDFF" }} />
                                    </MDBox>
                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{ color: "#FFFFFF" }}
                                    >
                                        {formatByCurrency(stats.billedByCurrency, convertCurrency, selectedCurrency)}
                                    </MDTypography>
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: "rgba(255,255,255,.68)" }}
                                    >
                                        {financeText("Gross profit")} {formatByCurrency(stats.profitByCurrency, convertCurrency, selectedCurrency)} · {financeText("Margin")}{" "}
                                        {stats.margin.toFixed(1)}%
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[
                                                { value: "en", label: "EN", flag: "/flags/gb.svg", title: "English" },
                                                { value: "tr", label: "TR", flag: "/flags/tr.svg", title: "Türkçe" },
                                            ].map((language) => (
                                                <MDBox
                                                    key={language.value}
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        changeFinanceLanguage({
                                                            target: { value: language.value },
                                                        })
                                                    }
                                                    title={language.title}
                                                    sx={{
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                        border: "none",
                                                        outline: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "7px",
                                                        height: 38,
                                                        minWidth: 76,
                                                        px: 1.15,
                                                        borderRadius: "8px",
                                                        background:
                                                            financeLanguage === language.value
                                                                ? "#FFFFFF"
                                                                : "transparent",
                                                        color:
                                                            financeLanguage === language.value
                                                                ? COLORS.navy
                                                                : "#FFFFFF",
                                                        boxShadow:
                                                            financeLanguage === language.value
                                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                                : "none",
                                                    }}
                                                >
                                                    <MDBox
                                                        component="img"
                                                        src={language.flag}
                                                        alt={language.title}
                                                        sx={{
                                                            width: 24,
                                                            height: 17,
                                                            objectFit: "cover",
                                                            borderRadius: "3px",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <MDTypography
                                                        component="span"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: "inherit",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {language.label}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={() => navigate("/invoice/finance/manual-invoices/new")}
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            <ReceiptLong sx={{ mr: 0.5 }} />
                                            {financeText("Create Invoice")}
                                        </MDButton>
                                        <Tooltip title={financeText("Refresh invoices")}>
                                            <IconButton
                                                onClick={fetchInvoices}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {loading ? (
                    <Grid container spacing={2.5}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <Grid item xs={12} sm={6} lg={3} key={index}>
                                <Skeleton
                                    variant="rounded"
                                    height={176}
                                    sx={{ borderRadius: "10px" }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <>
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <MetricCard
                                    title={financeText("Invoices")}
                                    value={stats.count}
                                    subtitle={financeText("Filtered invoice records")}
                                    icon={<ReceiptLong />}
                                    tone="navy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <MetricCard
                                    title={financeText("Revenue")}
                                    value={formatByCurrency(stats.billedByCurrency, convertCurrency, selectedCurrency)}
                                    subtitle={financeText("Total invoiced value")}
                                    icon={<ArrowUpward />}
                                    tone="blue"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <MetricCard
                                    title={financeText("Gross Profit")}
                                    value={formatByCurrency(stats.profitByCurrency, convertCurrency, selectedCurrency)}
                                    subtitle={`${financeText("Margin")} ${stats.margin.toFixed(1)}%`}
                                    icon={<ArrowUpward />}
                                    tone={stats.totalProfit >= 0 ? "green" : "red"}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <MetricCard
                                    title={financeText("PDF Coverage")}
                                    value={`${stats.pdfCount}/${stats.count}`}
                                    subtitle={`${stats.missingPdf} ${financeText("missing documents")}`}
                                    icon={<PictureAsPdf />}
                                    tone={stats.missingPdf ? "amber" : "green"}
                                />
                            </Grid>
                        </Grid>

                        {/* EDM gönderim durumu: kartlara tıklamak tabloyu o duruma göre filtreler */}
                        <MDBox mt={2.5}>
                            <EdmOverview
                                invoices={baseFilteredInvoices}
                                activeState={edmFilter}
                                onSelect={setEdmFilter}
                                formatAmount={formatEdmAmount}
                            />
                        </MDBox>



                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDBox
                                            width={38}
                                            height={38}
                                            borderRadius="11px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                border: "1px solid #B2DDFF",
                                            }}
                                        >
                                            <FilterAlt sx={{ fontSize: 19 }} />
                                        </MDBox>
                                        <MDBox>
                                            <MDTypography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ color: COLORS.text }}
                                            >
                                                {financeText("Invoice Filters")}
                                            </MDTypography>
                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText(
                                                    "Search and segment invoices by shipment, customer, transport mode and PDF status."
                                                )}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchInvoices}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <Refresh sx={{ mr: 0.5 }} /> Refresh
                                        </MDButton>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <RestartAlt sx={{ mr: 0.5 }} /> Reset
                                        </MDButton>
                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download sx={{ mr: 0.5 }} /> Export CSV
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, customer, email, invoice...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{ color: COLORS.subtext }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Transport Mode")}
                                            value={transportMode}
                                            onChange={(event) => setTransportMode(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                            <MenuItem value="Ocean">{financeText("Ocean")}</MenuItem>
                                            <MenuItem value="Road">{financeText("Road")}</MenuItem>
                                            <MenuItem value="Air">{financeText("Air")}</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("PDF Status")}
                                            value={pdfStatus}
                                            onChange={(event) => setPdfStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All PDF Statuses")}</MenuItem>
                                            <MenuItem value="available">{financeText("PDF Available")}</MenuItem>
                                            <MenuItem value="missing">{financeText("PDF Missing")}</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <EdmStatusSelect value={edmFilter} onChange={setEdmFilter} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <EdmProfileSelect value={edmProfile} onChange={setEdmProfile} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Currency")}
                                            value={selectedCurrency}
                                            onChange={(event) => setSelectedCurrency(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {CURRENCY_OPTIONS.map((currency) => (
                                                <MenuItem key={currency} value={currency}>
                                                    {currency}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Showing")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {filteredInvoices.length}
                                        </strong>{" "}
                                        {financeText("of")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {invoices.length}
                                        </strong>{" "}
                                        {financeText("invoices")}
                                    </MDTypography>
                                    <MDBox
                                        display="flex"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={1}
                                    >
                                        <Chip
                                            size="small"
                                            label={`${financeText("Revenue")} ${formatByCurrency(stats.revenueByCurrency, convertCurrency, selectedCurrency)}`}
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                        <Chip
                                            size="small"
                                            label={`${financeText("Cost")} ${formatByCurrency(stats.costByCurrency, convertCurrency, selectedCurrency)}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                        <Chip
                                            size="small"
                                            label={`${financeText("Profit")} ${formatByCurrency(stats.profitByCurrency, convertCurrency, selectedCurrency)}`}
                                            sx={{
                                                color:
                                                    stats.totalProfit >= 0 ? COLORS.green : COLORS.red,
                                                background:
                                                    stats.totalProfit >= 0
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 1500,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: "1px solid #F0F2F5 !important",
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,
                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="invoiceDetails"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={2.5} mt={0.1}>
                            {Object.entries(transportSummary).map(([mode, summary]) => (
                                <Grid item xs={12} md={4} key={mode}>
                                    <TransportInvoiceCard mode={mode} summary={summary} convertCurrency={convertCurrency} displayCurrency={selectedCurrency} />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            <Grid item xs={12} lg={8}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Revenue by Transport Mode")}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Invoice revenue and gross profit comparison.")}
                                        </MDTypography>

                                        <MDBox mt={2.5}>
                                            {Object.entries(transportSummary).map(
                                                ([mode, summary]) => {
                                                    const config =
                                                        MODE_CONFIG[mode] ||
                                                        MODE_CONFIG.Manual;
                                                    const maxRevenue = Math.max(
                                                        ...Object.values(transportSummary).map(
                                                            (item) => item.revenue,
                                                        ),
                                                        1,
                                                    );
                                                    const width = (summary.revenue / maxRevenue) * 100;

                                                    return (
                                                        <MDBox key={mode} mb={2}>
                                                            <MDBox
                                                                display="flex"
                                                                justifyContent="space-between"
                                                                mb={0.7}
                                                            >
                                                                <MDTypography
                                                                    variant="button"
                                                                    fontWeight="bold"
                                                                    sx={{ color: COLORS.text }}
                                                                >
                                                                    {financeText(mode)}
                                                                </MDTypography>
                                                                <MDTypography
                                                                    variant="caption"
                                                                    fontWeight="bold"
                                                                    sx={{ color: config.color }}
                                                                >
                                                                    {formatByCurrency(summary.revenueByCurrency, convertCurrency, selectedCurrency)} · {financeText("Profit")}{" "}
                                                                    {formatByCurrency(summary.profitByCurrency, convertCurrency, selectedCurrency)}
                                                                </MDTypography>
                                                            </MDBox>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={width}
                                                                sx={{
                                                                    height: 10,
                                                                    borderRadius: "999px",
                                                                    backgroundColor: "#F2F4F7",
                                                                    "& .MuiLinearProgress-bar": {
                                                                        borderRadius: "999px",
                                                                        background: config.color,
                                                                    },
                                                                }}
                                                            />
                                                        </MDBox>
                                                    );
                                                },
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={4}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Shipment Type Mix")}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Distribution of invoice shipment services.")}
                                        </MDTypography>

                                        <MDBox mt={2.2}>
                                            {shipmentTypeSummary.length ? (
                                                shipmentTypeSummary.map(([type, count]) => {
                                                    const percent = filteredInvoices.length
                                                        ? (count / filteredInvoices.length) * 100
                                                        : 0;
                                                    return (
                                                        <MDBox key={type} mb={1.65}>
                                                            <MDBox
                                                                display="flex"
                                                                justifyContent="space-between"
                                                                mb={0.45}
                                                            >
                                                                <MDTypography
                                                                    variant="button"
                                                                    fontWeight="bold"
                                                                    sx={{ color: COLORS.text }}
                                                                >
                                                                    {type}
                                                                </MDTypography>
                                                                <MDTypography
                                                                    variant="caption"
                                                                    sx={{ color: COLORS.subtext }}
                                                                >
                                                                    {count} · {percent.toFixed(1)}%
                                                                </MDTypography>
                                                            </MDBox>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={percent}
                                                                sx={{
                                                                    height: 7,
                                                                    borderRadius: "999px",
                                                                    backgroundColor: "#F2F4F7",
                                                                    "& .MuiLinearProgress-bar": {
                                                                        borderRadius: "999px",
                                                                        background: COLORS.navy,
                                                                    },
                                                                }}
                                                            />
                                                        </MDBox>
                                                    );
                                                })
                                            ) : (
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("No shipment type data available.")}
                                                </MDTypography>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Dialog
                open={Boolean(pendingEdmSend)}
                onClose={() => setPendingEdmSend(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: "10px", overflow: "hidden" } }}
            >
                <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    sx={{
                        p: 3,
                        background: `${COLORS.navy}`,
                    }}
                >
                    <MDBox
                        width={40}
                        height={40}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        sx={{ background: "rgba(255,255,255,.14)" }}
                    >
                        <Send sx={{ color: "#FFFFFF", fontSize: 20 }} />
                    </MDBox>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#FFFFFF" }}>
                        {financeText("Send to EDM")}
                    </MDTypography>
                </MDBox>

                <DialogContent sx={{ pt: 3 }}>
                    <MDTypography variant="button" fontWeight="regular" sx={{ color: COLORS.text }}>
                        {financeText(
                            "Send this invoice to EDM as e-Fatura/e-Arşiv? This cannot be undone.",
                        )}
                    </MDTypography>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => setPendingEdmSend(null)}
                    >
                        {financeText("Cancel")}
                    </MDButton>
                    <MDButton
                        variant="contained"
                        onClick={handleSendToEdm}
                        sx={{
                            background: `${COLORS.navy} !important`,
                            color: "#FFFFFF !important",
                        }}
                    >
                        <Send sx={{ mr: 0.5, fontSize: 18 }} />
                        {financeText("Send")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default InvoiceDetails;